/**
 * 霓虹蛇境 (Neon Snake Realm) - 游戏常量配置
 *
 * 本文件集中管理游戏中所有可配置的常量参数，
 * 包括网格尺寸、移动速度、道具参数、得分规则、
 * 粒子系统限制、颜色方案和方向向量等。
 */

import type { Direction, GameConfig } from '@/types/game';
import { PowerUpType } from '@/types/game';

// ============================================================================
// 方向向量常量
// ============================================================================

/**
 * 四个基本移动方向的单位向量。
 * 使用对象字面量确保类型安全，避免使用枚举以简化序列化。
 */
export const DIRECTION_VECTORS: Record<'UP' | 'DOWN' | 'LEFT' | 'RIGHT', Direction> = {
  /** 向上移动（Y 轴负方向） */
  UP: { x: 0, y: -1 },
  /** 向下移动（Y 轴正方向） */
  DOWN: { x: 0, y: 1 },
  /** 向左移动（X 轴负方向） */
  LEFT: { x: -1, y: 0 },
  /** 向右移动（X 轴正方向） */
  RIGHT: { x: 1, y: 0 },
} as const;

// ============================================================================
// 得分阈值常量
// ============================================================================

/**
 * 蛇身渐变色彩切换的得分阈值。
 * - 0 到 EARLY_MAX（不含）：青绿色系
 * - EARLY_MAX 到 MID_MAX（不含）：紫蓝色系
 * - MID_MAX 及以上：金橙色系
 */
export const SCORE_THRESHOLDS = {
  /** 初期渐变上限（0 ~ 50 分） */
  EARLY_MAX: 50,
  /** 中期渐变上限（51 ~ 150 分） */
  MID_MAX: 150,
} as const;

// ============================================================================
// 颜色方案常量
// ============================================================================

/**
 * 游戏全局颜色方案。
 * 所有颜色值均遵循视觉设计规范，确保霓虹灯管风格的统一性。
 */
export const COLOR_SCHEME = {
  /** 游戏背景色：深色太空蓝 */
  background: '#0a0a1a',

  /** 网格线颜色：微弱青色发光 */
  gridLine: 'rgba(0, 255, 255, 0.08)',

  /** 墙壁边界霓虹渐变：青色到品红 */
  wallBorder: ['#00ffff', '#ff00ff'] as readonly [string, string],

  /** 蛇身渐变 - 初期（0-50 分）：青绿到天蓝 */
  snakeGradientEarly: ['#00ff88', '#00ccff'] as readonly [string, string],

  /** 蛇身渐变 - 中期（51-150 分）：紫蓝到粉紫 */
  snakeGradientMid: ['#6600ff', '#ff00cc'] as readonly [string, string],

  /** 蛇身渐变 - 后期（151+ 分）：橙到金 */
  snakeGradientLate: ['#ff6600', '#ffcc00'] as readonly [string, string],

  /** 普通食物颜色：霓虹粉红 */
  food: '#ff3366',

  /**
   * 特殊道具颜色映射。
   * 每种道具类型对应唯一的视觉标识色。
   */
  powerUpColors: {
    [PowerUpType.Speed]: '#ffcc00',        // 加速药水：金黄
    [PowerUpType.Slow]: '#3399ff',         // 减速药水：蓝色
    [PowerUpType.WallPass]: '#cc33ff',     // 穿墙护盾：紫色
    [PowerUpType.DoubleScore]: '#ff9900',  // 双倍分数：橙色
        [PowerUpType.Shrink]: '#33ff66',       // 缩小药剂：绿色
    [PowerUpType.ClearObstacles]: '#ffffff', // 清除障碍物：白色
    } as const satisfies Record<PowerUpType, string>,

  /** 障碍物颜色：灰色 */
  obstacle: '#666666',
  /** 障碍物边框颜色 */
  obstacleBorder: '#888888',

  /**
   * 粒子系统颜色池。
   * 粒子爆炸时从此数组中随机采样颜色。
   */
  particleColors: [
    '#ff3366', // 霓虹粉红
    '#ffcc00', // 金黄
    '#00ff88', // 青绿
    '#00ccff', // 天蓝
    '#cc33ff', // 紫色
    '#ff9900', // 橙色
    '#33ff66', // 绿色
    '#ff6600', // 橙红
    '#6600ff', // 蓝紫
    '#ff00cc', // 粉紫
  ] as readonly string[],
} as const;

// ============================================================================
// 游戏配置常量
// ============================================================================

/**
 * 游戏全局可配置参数。
 *
 * 所有数值均经过调优，确保游戏体验平衡。
 * 修改这些值可调整游戏难度、视觉密度和性能表现。
 */
export const GAME_CONFIG: GameConfig = {
  // ---- 网格配置 ----
  /** 桌面端网格列数 */
  desktopGridWidth: 20,
  /** 桌面端网格行数 */
  desktopGridHeight: 20,
  /** 移动端网格列数 */
  mobileGridWidth: 15,
  /** 移动端网格行数 */
  mobileGridHeight: 15,

  // ---- 蛇的初始状态 ----
  /** 初始蛇身长度（节） */
  initialSnakeLength: 3,

  // ---- 移动速度 ----
  /** 基础移动间隔（毫秒），值越小蛇移动越快 */
  baseMoveInterval: 150,
  /** 加速药水速度倍率（> 1 表示加速） */
  speedMultiplier: 1.5,
  /** 减速药水速度倍率（< 1 表示减速） */
  slowMultiplier: 0.7,

  // ---- 道具效果持续时间 ----
  /** 加速/减速效果持续时间（毫秒） */
  speedEffectDuration: 8000,
  /** 穿墙护盾持续时间（毫秒） */
  wallPassDuration: 10000,
  /** 双倍分数持续时间（毫秒） */
  doubleScoreDuration: 10000,

  // ---- 缩小药剂 ----
  /** 缩小药剂缩短节数 */
  shrinkAmount: 3,
  /** 蛇身最小保留节数（防止缩小至无法游戏） */
  minSnakeLength: 3,

  // ---- 得分规则 ----
  /** 普通食物基础得分 */
  foodScore: 10,
  /** 连击判定时间窗口（毫秒），在此时间内连续吃食物触发连击 */
  comboWindow: 3000,
  /** 连击额外加分基数（连击数 × 此值 = 额外加分） */
  comboBonusBase: 5,

  // ---- 道具生成 ----
  /** 道具生成间隔（毫秒） */
  powerUpSpawnInterval: 15000,
  /** 道具存在时间（毫秒），超时自动消失 */
  powerUpLifetime: 15000,
  /** 同时激活道具效果上限 */
  maxActiveEffects: 3,

  // ---- 粒子系统 ----
  /** 粒子同时存在数量上限（桌面端） */
  maxParticlesDesktop: 200,
  /** 粒子同时存在数量上限（移动端） */
  maxParticlesMobile: 100,
  /** 吃到食物时生成的粒子数量范围 [最小值, 最大值] */
  eatParticleCount: [15, 25] as [number, number],
  /** 粒子生命周期范围（毫秒） [最小值, 最大值] */
  particleLifeRange: [500, 800] as [number, number],
  /** 粒子初始速度范围（像素/秒） [最小值, 最大值] */
  particleSpeedRange: [50, 200] as [number, number],

  // ---- 响应式断点 ----
    /** 移动端断点宽度（像素），视口宽度小于此值视为移动端 */
  mobileBreakpoint: 768,

  // ---- 障碍物配置 ----
  /** 初始生命数 */
  initialLives: 3,
  /** 每次吃食物生成的障碍物数量 */
  obstaclesPerEat: 2,
  /** 障碍物生成最大尝试次数 */
  obstacleSpawnMaxAttempts: 50,
} as const;

/**
 * 默认导出：游戏配置对象。
 * 方便在需要完整配置的模块中直接导入使用。
 */
export default GAME_CONFIG;